<template>
    <div ref="mapRoot" class="google-map" :class="{ 'google-map--fill': fillHeight, 'google-map--resizing': isResizingSidebar }">
        <aside ref="sidebar" class="sidebar" :style="sidebarStyle">
            <div class="sidebar__head">
                <div v-if="sidebarMode === 'packages'">Список пакетов</div>
                <div v-else-if="sidebarMode === 'layers'">Список слоёв</div>
                <div v-else>Список экранов</div>
                <div class="sidebar__head-actions">
                    <button
                        v-if="sidebarMode === 'screens'"
                        type="button"
                        class="filters-toggle"
                        :class="{ 'is-active': filtersOpen }"
                        :aria-expanded="filtersOpen ? 'true' : 'false'"
                        :aria-label="filtersOpen || !hasActiveFilters ? 'Фильтры' : 'Фильтры, есть активные'"
                        @click="filtersOpen = !filtersOpen"
                    >
                        Фильтры
                        <span
                            v-if="!filtersOpen && hasActiveFilters"
                            class="filters-toggle__dot"
                            aria-hidden="true"
                        ></span>
                    </button>
                    <img v-if="!hideRequiredStar && !selectedFrameGids.length" alt="red_star" class="is-empty"
                        src="/images/red_star.svg" />
                </div>
            </div>

            <!-- Packages mode -->
            <div v-if="sidebarMode === 'packages'" class="packages-list list-block">
                <div v-if="!packages.length" class="empty-hint">Нет доступных пакетов.</div>
                <div v-for="p in packages" :key="p.id" class="checkbox-item package-item">
                    <input type="radio" :id="'pkg-' + p.id" name="pkg-select" class="custom-checkbox"
                        :checked="String(selectedPackageId) === String(p.id)"
                        :disabled="readOnly"
                        @change="!readOnly && $emit('select-package', p)" />
                    <label :for="'pkg-' + p.id" class="checkbox">
                        <span class="checkbox__label">
                            {{ p.name }} <span class="package-count">({{ (p.frames || []).length }} экр.)</span>
                        </span>
                    </label>
                </div>
            </div>

            <div v-else-if="sidebarMode === 'layers'" class="packages-list list-block">
                <div v-if="!layers.length" class="empty-hint">Нет доступных слоёв.</div>
                <div v-for="layer in layers" :key="layer.id" class="layer-block">
                    <div class="checkbox-item layer-row">
                        <input
                            :id="'poi-layer-' + layer.id"
                            type="radio"
                            name="poi-layer-select"
                            class="custom-checkbox"
                            :checked="isLayerSelected(layer.id)"
                            :disabled="layerLocked"
                            @change="selectLayer(layer)"
                        />
                        <label :for="'poi-layer-' + layer.id" class="checkbox" :class="{ disabled: layerLocked }">
                            <span class="checkbox__label">
                                <span class="checkbox__title">{{ layer.name }}</span>
                                <span class="package-count">{{ layerScreenCount(layer) }} экр.</span>
                            </span>
                        </label>
                        <button
                            type="button"
                            class="layer-toggle"
                            :class="{ 'layer-toggle--open': isLayerExpanded(layer.id) }"
                            :aria-expanded="isLayerExpanded(layer.id) ? 'true' : 'false'"
                            :aria-label="isLayerExpanded(layer.id) ? 'Свернуть слой' : 'Показать экраны и точки'"
                            @click="toggleLayerExpand(layer.id)"
                        >
                            <img src="/images/arrow_down.svg" alt="" />
                        </button>
                    </div>
                    <div v-if="isLayerExpanded(layer.id)" class="layer-expand">
                        <p v-if="layerDetailState(layer.id) === 'loading'" class="layer-note">Загрузка…</p>
                        <p v-else-if="layerDetailState(layer.id) === 'error'" class="layer-note">Не удалось загрузить слой.</p>
                        <template v-else>
                            <p class="layer-expand__title">Точки</p>
                            <ul v-if="layerPoints(layer.id).length" class="layer-points">
                                <li v-for="(point, index) in layerPoints(layer.id)" :key="index">
                                    {{ index + 1 }}. {{ formatLayerPoint(point) }} · {{ point.radius_m }} м
                                </li>
                            </ul>
                            <p v-else class="layer-note">Точек нет.</p>
                            <p class="layer-expand__title">Экраны</p>
                            <div v-if="layerFrames(layer.id).length">
                                <GoogleMapScreenRow
                                    v-for="frame in layerFrames(layer.id)"
                                    :key="frame.gid"
                                    :frame="frame"
                                    :input-id="'layer-' + layer.id + '-frame-' + frame.gid"
                                    :checked="true"
                                    :disabled="true"
                                    :title="formatFrameDisplayLabel(frame)"
                                    :display-label="formatFrameDisplayLabel(frame)"
                                    :row-class="screenRowClass(frame)"
                                    :show-week-slot="showWeekRequests && weekReady && (showRequestDetails || !isWeekActive(frame.gid))"
                                    :week-active="isWeekActive(frame.gid)"
                                    :week-details="showRequestDetails"
                                    :week-expanded="openRequestGid === String(frame.gid)"
                                    :week-open="isRequestWeekOpen(frame)"
                                    :show-legacy-warning="showsLegacyWarning(frame)"
                                    :legacy-warning="frame.unavailable_message || ''"
                                    :show-inactive-badge="!disablePriceCheck && !frame.isActive"
                                    :package-name="screenPackageName(frame)"
                                    @focus="focusFrame(frame)"
                                    @toggle-week="toggleRequestWeek(frame.gid)"
                                />
                            </div>
                            <p v-else class="layer-note">Экранов нет.</p>
                        </template>
                    </div>
                </div>
            </div>

            <template v-else>
                <!-- Фильтры: город|улица, размер|тип -->
                <div v-if="filtersOpen" class="filters-section">
                    <div v-if="showOrganizationFilter" class="filter-group filter-group--wide">
                        <label class="filter-label">Организация</label>
                        <FilterMultiSelect
                            v-if="multiFilters"
                            :disabled="showOnlySelected"
                            v-model="organizationFilter"
                            :options="organizationOptions"
                            all-label="Все организации"
                        />
                        <select
                            v-else
                            class="filter-select"
                            :disabled="showOnlySelected"
                            :value="organizationFilter[0] || ''"
                            @change="setSingleFilter('organizationFilter', $event.target.value)"
                        >
                            <option value="">Все организации</option>
                            <option v-for="organization in organizationOptions" :key="organization.value" :value="organization.value">
                                {{ organization.label }}
                            </option>
                        </select>
                    </div>
                    <div class="filter-group">
                        <label class="filter-label">Город</label>
                        <FilterMultiSelect
                            v-if="multiFilters"
                            :disabled="showOnlySelected"
                            v-model="cityFilter"
                            :options="cityOptions"
                            all-label="Все города"
                        />
                        <select
                            v-else
                            class="filter-select"
                            :disabled="showOnlySelected"
                            :value="cityFilter[0] || ''"
                            @change="setSingleFilter('cityFilter', $event.target.value)"
                        >
                            <option value="">Все города</option>
                            <option v-for="city in cityOptions" :key="city.value" :value="city.value">
                                {{ city.label }}
                            </option>
                        </select>
                    </div>
                    <div class="filter-group">
                        <label class="filter-label">Улица</label>
                        <FilterMultiSelect
                            v-if="multiFilters"
                            :disabled="showOnlySelected"
                            v-model="streetFilter"
                            :options="streetOptions"
                            all-label="Все улицы"
                        />
                        <select
                            v-else
                            class="filter-select"
                            :disabled="showOnlySelected"
                            :value="streetFilter[0] || ''"
                            @change="setSingleFilter('streetFilter', $event.target.value)"
                        >
                            <option value="">Все улицы</option>
                            <option v-for="street in streetOptions" :key="street.value" :value="street.value">
                                {{ street.label }}
                            </option>
                        </select>
                    </div>
                    <div class="filter-group">
                        <label class="filter-label">Размер экрана</label>
                        <FilterMultiSelect
                            v-if="multiFilters"
                            :disabled="showOnlySelected"
                            v-model="sizeFilter"
                            :options="sizeOptions"
                            all-label="Все размеры"
                        />
                        <select
                            v-else
                            class="filter-select"
                            :disabled="showOnlySelected"
                            :value="sizeFilter[0] || ''"
                            @change="setSingleFilter('sizeFilter', $event.target.value)"
                        >
                            <option value="">Все размеры</option>
                            <option v-for="size in sizeOptions" :key="size.value" :value="size.value">
                                {{ size.label }}
                            </option>
                        </select>
                    </div>
                    <div class="filter-group">
                        <label class="filter-label">Тип</label>
                        <FilterMultiSelect
                            v-if="multiFilters"
                            :disabled="showOnlySelected"
                            v-model="typeFilter"
                            :options="typeOptions"
                            all-label="Все типы"
                        />
                        <select
                            v-else
                            class="filter-select"
                            :disabled="showOnlySelected"
                            :value="typeFilter[0] || ''"
                            @change="setSingleFilter('typeFilter', $event.target.value)"
                        >
                            <option value="">Все типы</option>
                            <option v-for="constructionType in typeOptions" :key="constructionType.value" :value="constructionType.value">
                                {{ constructionType.label }}
                            </option>
                        </select>
                    </div>

                    <div v-if="canManageTestFrames" class="filter-group filter-group--checkbox">
                        <label class="filter-label filter-label--checkbox">
                            <input
                                type="checkbox"
                                v-model="showTestFrames"
                                class="custom-checkbox"
                                :disabled="showOnlySelected"
                            />
                            Показывать тестовые
                        </label>
                    </div>

                    <div
                        class="filter-group filter-group--checkbox"
                        :class="{ 'filter-group--wide filter-group--toggles': showWeekRequests }"
                    >
                        <button
                            v-if="showWeekRequests"
                            type="button"
                            class="only-selected-btn"
                            :class="{ 'is-active': showOnlyEnabled }"
                            :aria-pressed="showOnlyEnabled ? 'true' : 'false'"
                            :disabled="showOnlySelected"
                            @click="showOnlyEnabled = !showOnlyEnabled"
                        >
                            Включенные
                        </button>
                        <button
                            type="button"
                            class="only-selected-btn"
                            :class="{ 'is-active': showOnlySelected }"
                            :aria-pressed="showOnlySelected ? 'true' : 'false'"
                            :disabled="selectedFrameGids.length === 0"
                            @click="toggleOnlySelected"
                        >
                            Выбранные
                        </button>
                    </div>

                    <button
                        type="button"
                        @click="clearFilters"
                        class="clear-filters-btn"
                        :disabled="!hasActiveFilters"
                    >
                        Сбросить фильтры
                    </button>
                </div>
                <div v-if="multiFilters && !readOnly" class="bulk-select bulk-select--above-list">
                    <button
                        type="button"
                        class="bulk-select__btn"
                        :disabled="!canSelectFiltered"
                        @click="selectFilteredFrames"
                    >
                        Выбрать все
                    </button>
                    <button
                        type="button"
                        class="bulk-select__btn"
                        :disabled="!canDeselectFiltered"
                        @click="deselectFilteredFrames"
                    >
                        Убрать все
                    </button>
                </div>
                <div class="screens-list list-block">
                    <GoogleMapScreenRow
                        v-for="frame in sortedFrames"
                        :key="frame.gid"
                        :frame="frame"
                        :input-id="'frame-' + frame.gid"
                        :checked="isSelected(frame.gid)"
                        :disabled="readOnly || (!disablePriceCheck && !frame.isActive) || isFrameSspBlocked(frame)"
                        :title="getFrameLabel(frame.gid)"
                        :display-label="formatFrameDisplayLabel(frame)"
                        :row-class="screenRowClass(frame)"
                        :show-week-slot="showWeekRequests && weekReady && (showRequestDetails || !isWeekActive(frame.gid))"
                        :week-active="isWeekActive(frame.gid)"
                        :week-details="showRequestDetails"
                        :week-expanded="openRequestGid === String(frame.gid)"
                        :week-open="isRequestWeekOpen(frame)"
                        :show-legacy-warning="showsLegacyWarning(frame)"
                        :legacy-warning="frame.unavailable_message || ''"
                        :show-inactive-badge="!disablePriceCheck && !frame.isActive"
                        :package-name="screenPackageName(frame)"
                        @toggle="toggleFrameSelection(frame.gid)"
                        @focus="focusFrame(frame)"
                        @toggle-week="toggleRequestWeek(frame.gid)"
                    />
                </div>
            </template>

            <div class="selected-info" aria-live="polite">
                <p v-if="sidebarMode === 'packages' || sidebarMode === 'layers'">
                    Выбрано экранов: <strong>{{ selectedFrameGids.length }}</strong>
                </p>
                <p v-else>
                    Выбрано экранов:
                    <strong>{{ selectedFrameGids.length }}</strong> из {{ visibleFramesCount }}
                </p>
                <div v-if="allowBulkSelect && sidebarMode === 'screens' && !readOnly" class="bulk-select">
                    <button type="button" class="bulk-select__btn" @click="selectAllVisible">
                        Выбрать все видимые
                    </button>
                    <button type="button" class="bulk-select__btn" :disabled="!selectedFrameGids.length"
                        @click="clearSelection">
                        Сбросить
                    </button>
                </div>
            </div>
        </aside>

        <div
            v-if="isMapFullscreen"
            class="sidebar-resizer"
            role="separator"
            aria-orientation="vertical"
            aria-label="Ширина списка экранов"
            @pointerdown="startSidebarResize"
        />

        <div class="map-container">
            <button type="button" class="map-fullscreen-btn" @click="toggleMapFullscreen">
                {{ isMapFullscreen ? 'Свернуть' : 'На весь экран' }}
            </button>
            <GMapMap ref="gmap" :center="{ lat: 48.11, lng: 66.22 }" :zoom="4" :options="{ fullscreenControl: false }">
                <GMapInfoWindow
                    v-if="hoveredFrame"
                    :key="hoveredFrame.gid"
                    :position="{ lat: Number(hoveredFrame.lat) || 0, lng: Number(hoveredFrame.lon) || 0 }"
                    :options="infoWindowOptions()"
                    :opened="true"
                >
                    <div class="info-window-content">
                        <div class="info-window-img-wrap" v-if="hoveredFrame.path">
                            <img :src="hoveredFrame.path" alt="screen" class="info-window-img"
                                @click.stop="openFullscreen(hoveredFrame.path)" title="Открыть полноэкранно" />
                        </div>
                        <p class="info-window-heading">
                            {{ formatFrameDisplayLabel(hoveredFrame) }}
                            <span v-if="hoveredFrame.is_test" class="test-badge">Тест</span>
                        </p>
                        <p v-if="!disablePriceCheck && !hoveredFrame.isActive" class="info-window-warning">
                            ⚠️ Нет ценовых пакетов
                        </p>
                        <p v-if="hoveredFrame.unavailable_message" class="info-window-unavailable">
                            {{ hoveredFrame.unavailable_message }}
                        </p>
                        <p class="info-window-meta">
                            Физический размер экрана:
                            {{ hoveredFrame.physical_size_width }} x
                            {{ hoveredFrame.physical_size_height }}
                        </p>
                        <p class="info-window-meta">
                            Требуемый размер креатива:
                            {{ hoveredFrame.width }} x
                            {{ hoveredFrame.height }}
                        </p>
                        <p class="info-window-meta">
                            GID:
                            {{ hoveredFrame.gid }}
                        </p>
                    </div>
                </GMapInfoWindow>
            </GMapMap>
        </div>
        <div v-if="fullscreenImageUrl" class="img-overlay" @click.self="closeFullscreen">
            <button class="img-overlay-close" @click="closeFullscreen">✕</button>
            <img :src="fullscreenImageUrl" alt="screen full" />
        </div>
    </div>
</template>

<script>
import {
    Map as GMapMap,
    InfoWindow as GMapInfoWindow,
} from '@fawmi/vue-google-maps';
import axios from 'axios';
import FilterMultiSelect from './FilterMultiSelect.vue';
import GoogleMapScreenRow from './GoogleMapScreenRow.vue';
import { createFrameMarkerLayer } from '../../utils/frameMarkerLayer';
import { createPoiPointLayer } from '../../utils/poiPointLayer';

const SIDEBAR_MIN_PX = 280;
const MAP_MIN_PX = 280;

export default {
    name: 'GoogleMap',
    components: { GMapMap, GMapInfoWindow, FilterMultiSelect, GoogleMapScreenRow },
    props: {
        frames: { type: Array, required: true },
        // (city filtering is handled by parent components when needed)
        selectedFrameGids: { type: Array, default: () => [] },
        disablePriceCheck: { type: Boolean, default: false },
        selectedCreatives: { type: Array, default: () => [] },
        hideRequiredStar: { type: Boolean, default: false }, // Скрыть красную звездочку
        allowMultiSsp: { type: Boolean, default: false }, // RTB: разрешить экраны разных SSP в одной кампании
        // New props to support package sidebar mode
        packages: { type: Array, default: () => [] },
        sidebarMode: { type: String, default: 'screens' }, // 'screens' | 'packages' | 'layers'
        selectedPackageId: { type: [Number, String], default: null },
        layers: { type: Array, default: () => [] },
        selectedLayerId: { type: [Number, String], default: null },
        layerLocked: { type: Boolean, default: false },
        layerDetails: { type: Object, default: () => ({}) },
        readOnly: { type: Boolean, default: false },
        fillHeight: { type: Boolean, default: false },
        allowBulkSelect: { type: Boolean, default: false },
        canManageTestFrames: { type: Boolean, default: false },
        multiFilters: { type: Boolean, default: false },
        showOrganizationFilter: { type: Boolean, default: false },
        showWeekRequests: { type: Boolean, default: false },
        showRequestDetails: { type: Boolean, default: true },
        points: { type: Array, default: () => [] },
        pointsDraggable: { type: Boolean, default: false },
    },
    emits: ['update:selectedFrameGids', 'select-package', 'select-layer', 'expand-layer', 'map-click', 'point-move'],
    data() {
        return {
            hoveredId: null,
            sizeFilter: [],
            organizationFilter: [],
            showOnlySelected: false,
            showOnlyEnabled: true,
            cityFilter: [],
            streetFilter: [],
            typeFilter: [],
            showTestFrames: false,
            filtersOpen: false,
            fullscreenImageUrl: null,
            isMapFullscreen: false,
            isResizingSidebar: false,
            sidebarWidthPx: null,
            sidebarResizeRaf: 0,
            sidebarResizeCleanup: null,
            weekActiveGids: [],
            weekReady: false,
            weekUnavailable: false,
            weekRequestSeq: 0,
            openRequestGid: null,
            expandedLayerId: null,
        };
    },
    computed: {
        weekGidKey() {
            if (!this.showWeekRequests || this.sidebarMode === 'packages') {
                return '';
            }
            const gids = (this.frames || []).map(frame => String(frame.gid || '')).filter(Boolean);
            if (this.sidebarMode === 'layers' && this.expandedLayerId != null) {
                this.layerFrames(this.expandedLayerId).forEach((frame) => {
                    const gid = String(frame?.gid || '');
                    if (gid) gids.push(gid);
                });
            }
            return [...new Set(gids)].join('\0');
        },
        weekActiveSet() {
            return new Set(this.weekActiveGids);
        },
        sidebarStyle() {
            if (!this.isMapFullscreen || this.sidebarWidthPx == null) {
                return null;
            }

            return { width: `${this.sidebarWidthPx}px` };
        },
        hasActiveFilters() {
            return (
                this.sizeFilter.length > 0
                || (this.showOrganizationFilter && this.organizationFilter.length > 0)
                || this.showOnlySelected
                || this.cityFilter.length > 0
                || this.streetFilter.length > 0
                || this.typeFilter.length > 0
                || (this.canManageTestFrames && this.showTestFrames)
                || (this.showWeekRequests && !this.showOnlyEnabled)
            );
        },
        /**
         * Frames before the dropdown filters: unknown city, test, SSP lock.
         * Faceted option lists and filteredFrames both start from this pool.
         */
        baseCandidateFrames() {
            let frames = (Array.isArray(this.frames) ? this.frames : [])
                .filter((frame) => frame && !this.isUnknownCity(frame));

            if (this.canManageTestFrames && !this.showTestFrames) {
                frames = frames.filter((frame) => !frame.is_test);
            }

            const selectedSSP = this.selectedSSP;
            if (!this.allowMultiSsp && selectedSSP) {
                frames = frames.filter((frame) => frame.ssp === selectedSSP);
            }

            return frames;
        },
        selectedCreativeSizes() {
            if (!Array.isArray(this.selectedCreatives)) {
                return [];
            }
            return this.selectedCreatives
                .filter((c) => c && c.width != null && c.height != null)
                .map((c) => ({ width: Number(c.width), height: Number(c.height) }))
                .filter((c) => !Number.isNaN(c.width) && !Number.isNaN(c.height));
        },
        framesForOrganizationOptions() {
            return this.baseCandidateFrames.filter((frame) => (
                !frame.is_test
                && this.frameMatchesCity(frame)
                && this.frameMatchesStreet(frame)
                && this.frameMatchesSize(frame)
                && this.frameMatchesType(frame)
            ));
        },
        framesForCityOptions() {
            return this.baseCandidateFrames.filter((frame) => (
                this.frameMatchesOrganization(frame)
                && this.frameMatchesSize(frame)
                && this.frameMatchesStreet(frame)
                && this.frameMatchesType(frame)
            ));
        },
        framesForStreetOptions() {
            return this.baseCandidateFrames.filter((frame) => (
                this.frameMatchesOrganization(frame)
                && this.frameMatchesCity(frame)
                && this.frameMatchesSize(frame)
                && this.frameMatchesType(frame)
            ));
        },
        framesForSizeOptions() {
            return this.baseCandidateFrames.filter((frame) => (
                this.frameMatchesOrganization(frame)
                && this.frameMatchesCity(frame)
                && this.frameMatchesStreet(frame)
                && this.frameMatchesType(frame)
            ));
        },
        framesForTypeOptions() {
            return this.baseCandidateFrames.filter((frame) => (
                this.frameMatchesOrganization(frame)
                && this.frameMatchesCity(frame)
                && this.frameMatchesStreet(frame)
                && this.frameMatchesSize(frame)
            ));
        },
        uniqueCities() {
            const byId = new Map();
            this.framesForCityOptions.forEach((frame) => {
                if (frame.city_id == null || frame.city_id === '') return;
                const id = String(frame.city_id);
                if (byId.has(id)) return;
                const name = (frame.city && String(frame.city).trim()) || id;
                byId.set(id, { id, name });
            });
            return Array.from(byId.values()).sort((a, b) =>
                a.name.localeCompare(b.name, 'ru', { sensitivity: 'base' }),
            );
        },
        uniqueStreets() {
            const byKey = new Map();
            this.framesForStreetOptions.forEach((frame) => {
                const pairs = [
                    [frame.street_1_key, frame.street_1_name || frame.street_1],
                    [frame.street_2_key, frame.street_2_name || frame.street_2],
                ];
                pairs.forEach(([key, name]) => {
                    if (key == null || key === '') return;
                    const id = String(key);
                    if (byKey.has(id)) return;
                    const label = name != null && String(name).trim() !== '' ? String(name).trim() : id;
                    byKey.set(id, { id, name: label });
                });
            });
            return Array.from(byKey.values()).sort((a, b) =>
                a.name.localeCompare(b.name, 'ru', { sensitivity: 'base' }),
            );
        },
        uniqueSizes() {
            const sizes = new Set();
            this.framesForSizeOptions.forEach((frame) => {
                if (frame && frame.width && frame.height) {
                    sizes.add(`${frame.width}×${frame.height}`);
                }
            });
            return Array.from(sizes).sort();
        },
        uniqueTypes() {
            const types = new Map();
            this.framesForTypeOptions.forEach((frame) => {
                const code = frame.construction_type != null
                    ? String(frame.construction_type).trim()
                    : '';
                if (!code) return;
                const label = String(frame.construction_type_label || code).trim() || code;
                if (!types.has(code)) {
                    types.set(code, { value: code, label });
                }
            });
            return Array.from(types.values()).sort((a, b) =>
                a.label.localeCompare(b.label, 'ru', { sensitivity: 'base' }),
            );
        },
        uniqueOrganizations() {
            const sources = new Map();
            this.framesForOrganizationOptions.forEach((frame) => {
                const value = frame?.oid != null ? String(frame.oid).trim() : '';
                if (!value || sources.has(value)) return;
                const label = String(frame.organization_name || '').trim() || value;
                sources.set(value, { value, label });
            });
            return Array.from(sources.values()).sort((a, b) =>
                a.label.localeCompare(b.label, 'ru', { sensitivity: 'base' }),
            );
        },
        filterLabels() {
            const cities = new Map();
            const streets = new Map();
            const sizes = new Map();
            const types = new Map();
            const organizations = new Map();

            this.baseCandidateFrames.forEach((frame) => {
                if (frame.city_id != null && frame.city_id !== '') {
                    const id = String(frame.city_id);
                    if (!cities.has(id)) {
                        cities.set(id, (frame.city && String(frame.city).trim()) || id);
                    }
                }

                [
                    [frame.street_1_key, frame.street_1_name || frame.street_1],
                    [frame.street_2_key, frame.street_2_name || frame.street_2],
                ].forEach(([key, name]) => {
                    if (key == null || key === '') return;
                    const id = String(key);
                    if (streets.has(id)) return;
                    const label = name != null && String(name).trim() !== '' ? String(name).trim() : id;
                    streets.set(id, label);
                });

                if (frame.width && frame.height) {
                    const size = `${frame.width}×${frame.height}`;
                    sizes.set(size, size);
                }

                const code = frame.construction_type != null
                    ? String(frame.construction_type).trim()
                    : '';
                if (code && !types.has(code)) {
                    types.set(code, String(frame.construction_type_label || code).trim() || code);
                }

                const oid = frame?.oid != null ? String(frame.oid).trim() : '';
                if (oid && !frame.is_test && !organizations.has(oid)) {
                    organizations.set(oid, String(frame.organization_name || '').trim() || oid);
                }
            });

            return { cities, streets, sizes, types, organizations };
        },
        organizationOptions() {
            return this.mergeSelectedOptions(
                this.uniqueOrganizations,
                this.organizationFilter,
                (value) => this.filterLabels.organizations.get(value) || value,
            );
        },
        cityOptions() {
            const facet = this.uniqueCities.map((city) => ({ value: city.id, label: city.name }));
            return this.mergeSelectedOptions(
                facet,
                this.cityFilter,
                (value) => this.filterLabels.cities.get(value) || value,
            );
        },
        streetOptions() {
            const facet = this.uniqueStreets.map((street) => ({ value: street.id, label: street.name }));
            return this.mergeSelectedOptions(
                facet,
                this.streetFilter,
                (value) => this.filterLabels.streets.get(value) || value,
            );
        },
        sizeOptions() {
            const facet = this.uniqueSizes.map((size) => ({ value: size, label: size }));
            return this.mergeSelectedOptions(
                facet,
                this.sizeFilter,
                (value) => this.filterLabels.sizes.get(value) || value,
            );
        },
        typeOptions() {
            return this.mergeSelectedOptions(
                this.uniqueTypes,
                this.typeFilter,
                (value) => this.filterLabels.types.get(value) || value,
            );
        },
        selectableFilteredFrames() {
            return (this.sortedFrames || [])
                .filter((frame) => this.disablePriceCheck || frame.isActive)
                .filter((frame) => !this.isFrameSspBlocked(frame));
        },
        selectedGidSet() {
            return new Set((this.selectedFrameGids || []).map((gid) => String(gid)));
        },
        canSelectFiltered() {
            return this.selectableFilteredFrames.some((frame) => !this.selectedGidSet.has(String(frame.gid)));
        },
        canDeselectFiltered() {
            return this.selectableFilteredFrames.some((frame) => this.selectedGidSet.has(String(frame.gid)));
        },
        // Количество фреймов, отображаемых в сайдбаре (учитывает режим пакетов и фильтры)
        visibleFramesCount() {
            if (this.sidebarMode === 'packages' && this.selectedPackageId) {
                const pkg = (this.packages || []).find(p => String(p.id) === String(this.selectedPackageId));
                return pkg && Array.isArray(pkg.frames) ? (pkg.frames || []).length : 0;
            }

            return (this.sortedFrames || []).length;
        },
        // Определяем SSP выбранных экранов
        selectedSSP() {
            if (this.selectedFrameGids.length === 0) {
                return null;
            }

            // Находим SSP первого выбранного экрана
            const firstSelectedGid = this.selectedFrameGids[0];
            const firstFrame = this.frames.find(f => String(f.gid) === String(firstSelectedGid));

            if (!firstFrame || !firstFrame.ssp) {
                return null;
            }

            // Проверяем, что все выбранные экраны из одной SSP
            const selectedFrames = this.selectedFrameGids.map(gid => {
                return this.frames.find(f => String(f.gid) === String(gid));
            }).filter(Boolean);

            const sspSet = new Set(selectedFrames.map(f => f.ssp).filter(Boolean));

            // Если все экраны из одной SSP, возвращаем её
            return sspSet.size === 1 ? Array.from(sspSet)[0] : null;
        },
        // Применяем фильтры к фреймам
        filteredFrames() {
            if (this.showOnlySelected) {
                const selected = this.selectedGidSet;
                return (Array.isArray(this.frames) ? this.frames : [])
                    .filter((frame) => frame && selected.has(String(frame.gid)));
            }

            return this.baseCandidateFrames.filter((frame) => (
                this.frameMatchesOrganization(frame)
                && this.frameMatchesCity(frame)
                && this.frameMatchesStreet(frame)
                && this.frameMatchesSize(frame)
                && this.frameMatchesType(frame)
                && this.frameMatchesEnabled(frame)
            ));
        },
        sortedFrames() {
            const frames = this.filteredFrames || [];

            if (this.showOnlySelected || !this.selectedCreatives.length) {
                const packageFrames = frames.filter(f => f.isInPackage);
                const otherFrames = frames.filter(f => !f.isInPackage);
                return [...this.sortFramesByGid(packageFrames), ...this.sortFramesByGid(otherFrames)];
            }

            // Получаем размеры выбранных креативов (приводим к числам)
            const creativeSizes = this.selectedCreativeSizes;

            // Если нет размеров креативов — приоритизируем пакеты как раньше
            if (!creativeSizes.length) {
                const packageFrames = frames.filter(f => f.isInPackage);
                const otherFrames = frames.filter(f => !f.isInPackage);
                return [...this.sortFramesByGid(packageFrames), ...this.sortFramesByGid(otherFrames)];
            }

            // Совпавшие с креативом экраны выше, остальные остаются в списке.
            const matchingPackage = [];
            const nonMatchingPackage = [];
            const matchingOthers = [];
            const nonMatchingOthers = [];

            frames.forEach(frame => {
                const matches = this.frameMatchesCreativeSizes(frame, creativeSizes);

                if (frame.isInPackage) {
                    if (matches) matchingPackage.push(frame);
                    else nonMatchingPackage.push(frame);
                } else {
                    if (matches) matchingOthers.push(frame);
                    else nonMatchingOthers.push(frame);
                }
            });

            return [
                ...this.sortFramesByGid(matchingPackage),
                ...this.sortFramesByGid(nonMatchingPackage),
                ...this.sortFramesByGid(matchingOthers),
                ...this.sortFramesByGid(nonMatchingOthers),
            ];
        },
        // Frames actually shown on the map (honours package sidebar mode)
        mapFrames() {
            // If in packages mode and a package is selected, show only frames from that package
            if (this.sidebarMode === 'packages' && this.selectedPackageId) {
                const pkg = (this.packages || []).find(p => String(p.id) === String(this.selectedPackageId));
                if (!pkg || !Array.isArray(pkg.frames)) return [];
                const gids = new Set((pkg.frames || []).map(f => String(f.gid)));
                const packageFrames = (this.frames || []).filter(f => gids.has(String(f.gid)));
                const filteredGids = new Set((this.filteredFrames || []).map(f => String(f.gid)));
                return packageFrames.filter(f => filteredGids.has(String(f.gid)));
            }

            // Otherwise show sortedFrames (screens mode)
            return this.sortedFrames || [];
        },
        markerFrames() {
            return (this.mapFrames || []).map((frame) => ({
                gid: frame.gid,
                lat: frame.lat,
                lng: frame.lon,
                azimuth: frame.azimuth,
                state: this.markerState(frame),
                clickable: this.isMarkerClickable(frame),
            }));
        },
        hoveredFrame() {
            if (this.hoveredId == null) {
                return null;
            }

            return (this.mapFrames || []).find((frame) => String(frame.gid) === String(this.hoveredId)) || null;
        },
    },
    // lifecycle hooks
    methods: {
        isWeekActive(gid) {
            return this.weekActiveSet.has(String(gid));
        },
        isRequestWeekOpen(frame) {
            return this.showRequestDetails
                && this.isWeekActive(frame.gid)
                && this.openRequestGid === String(frame.gid);
        },
        toggleRequestWeek(gid) {
            const key = String(gid);
            this.openRequestGid = this.openRequestGid === key ? null : key;
        },
        showsLegacyWarning(frame) {
            if (!frame?.unavailable_message) {
                return false;
            }
            if (!this.showWeekRequests || this.sidebarMode === 'packages') {
                return true;
            }
            return this.weekUnavailable;
        },
        async refreshWeekActive() {
            const seq = ++this.weekRequestSeq;
            if (!this.weekGidKey) {
                this.weekActiveGids = [];
                this.weekReady = false;
                this.weekUnavailable = false;
                return;
            }

            const gids = this.weekGidKey.split('\0');
            const active = [];
            try {
                for (let offset = 0; offset < gids.length; offset += 2000) {
                    const { data } = await axios.post('/screens/request-hours', {
                        gids: gids.slice(offset, offset + 2000),
                        week: false,
                    });
                    if (seq !== this.weekRequestSeq) {
                        return;
                    }
                    if (data?.available === false) {
                        this.weekUnavailable = true;
                        this.weekReady = false;
                        this.weekActiveGids = [];
                        return;
                    }
                    active.push(...(data?.gids || []).map(gid => String(gid)));
                }
            } catch {
                if (seq !== this.weekRequestSeq) {
                    return;
                }
                this.weekUnavailable = true;
                this.weekReady = false;
                this.weekActiveGids = [];
                return;
            }

            if (seq !== this.weekRequestSeq) {
                return;
            }
            this.weekActiveGids = active;
            this.weekUnavailable = false;
            this.weekReady = true;
        },
        sortFramesByGid(frames) {
            return [...frames].sort((a, b) => String(a.gid).localeCompare(String(b.gid), undefined, { numeric: true }));
        },
        frameMatchesEnabled(frame) {
            if (!this.showOnlyEnabled || !this.showWeekRequests || !this.weekReady) {
                return true;
            }

            return this.isWeekActive(frame.gid);
        },
        frameMatchesOrganization(frame) {
            if (!this.showOrganizationFilter || this.organizationFilter.length === 0) return true;
            const oid = frame?.oid != null ? String(frame.oid).trim() : '';
            return oid !== '' && this.organizationFilter.includes(oid);
        },
        frameMatchesCity(frame) {
            if (this.cityFilter.length === 0) return true;
            return this.cityFilter.includes(String(frame.city_id));
        },
        frameMatchesStreet(frame) {
            if (this.streetFilter.length === 0) return true;
            const selected = new Set(this.streetFilter);
            return selected.has(String(frame.street_1_key || ''))
                || selected.has(String(frame.street_2_key || ''));
        },
        frameMatchesSize(frame) {
            if (this.sizeFilter.length === 0) return true;
            if (!frame?.width || !frame?.height) return false;
            return this.sizeFilter.includes(`${frame.width}×${frame.height}`);
        },
        frameMatchesType(frame) {
            if (this.typeFilter.length === 0) return true;
            return this.typeFilter.includes(String(frame.construction_type || ''));
        },
        mergeSelectedOptions(options, selected, labelOf) {
            const present = new Set(options.map((option) => option.value));
            const extras = selected
                .filter((value) => !present.has(value))
                .map((value) => ({ value, label: labelOf(value) }));
            return extras.length === 0 ? options : [...options, ...extras];
        },
        setSingleFilter(field, value) {
            this[field] = value ? [String(value)] : [];
        },
        frameMatchesCreativeSizes(frame, creativeSizes) {
            const fw = Number(frame.width);
            const fh = Number(frame.height);
            if (Number.isNaN(fw) || Number.isNaN(fh)) return false;
            return creativeSizes.some((cs) => cs.width === fw && cs.height === fh);
        },
        isSelected(gid) {
            return this.selectedFrameGids.some(sgid => String(sgid) === String(gid));
        },
        screenRowClass(frame) {
            return {
                inactive: !this.disablePriceCheck && !frame.isActive,
                'ssp-filtered': this.isFrameSspBlocked(frame),
                'in-package': Boolean(frame.isInPackage) && this.sidebarMode !== 'screens',
                'is-test': !!frame.is_test,
            };
        },
        screenPackageName(frame) {
            if (this.sidebarMode === 'screens' || !frame?.packageNames?.length) {
                return '';
            }
            return String(frame.packageNames[0]);
        },
        isLayerSelected(id) {
            return this.selectedLayerId != null && String(this.selectedLayerId) === String(id);
        },
        isLayerExpanded(id) {
            return this.expandedLayerId != null && String(this.expandedLayerId) === String(id);
        },
        layerScreenCount(layer) {
            const count = Number(layer?.screens_count ?? layer?.resolved_frames_count ?? layer?.screens);
            return Number.isFinite(count) ? count : 0;
        },
        selectLayer(layer) {
            if (this.layerLocked) {
                return;
            }
            this.$emit('select-layer', layer);
        },
        toggleLayerExpand(id) {
            if (this.isLayerExpanded(id)) {
                this.expandedLayerId = null;
                return;
            }
            this.expandedLayerId = id;
            const detail = this.layerDetail(id);
            if (!detail || detail.error) {
                this.$emit('expand-layer', id);
            }
        },
        layerDetail(id) {
            const details = this.layerDetails || {};
            return details[id] || details[String(id)] || null;
        },
        layerDetailState(id) {
            const detail = this.layerDetail(id);
            if (!detail) return 'loading';
            if (detail.error) return 'error';
            return 'ready';
        },
        layerPoints(id) {
            const points = this.layerDetail(id)?.points;
            return Array.isArray(points) ? points : [];
        },
        layerFrames(id) {
            const frames = this.layerDetail(id)?.frames;
            return Array.isArray(frames) ? this.sortFramesByGid(frames) : [];
        },
        formatLayerPoint(point) {
            const lat = Number(point?.lat);
            const lng = Number(point?.lng);
            const latText = Number.isFinite(lat) ? lat.toFixed(6) : '—';
            const lngText = Number.isFinite(lng) ? lng.toFixed(6) : '—';
            return `${latText}, ${lngText}`;
        },
        toggleFrameSelection(gid) {
            if (this.readOnly || this.sidebarMode === 'layers') {
                return;
            }
            const normalizedGid = String(gid);
            const frame = this.frames.find(f => String(f.gid) === normalizedGid);

            // If we're in package sidebar mode and the frame belongs to the selected package,
            // do not allow toggling selection (package frames must remain selected).
            if (this.sidebarMode === 'packages' && this.selectedPackageId) {
                const pkg = (this.packages || []).find(p => String(p.id) === String(this.selectedPackageId));
                if (pkg && Array.isArray(pkg.frames) && pkg.frames.some(f => String(f.gid) === normalizedGid)) {
                    return;
                }
            }

            // Не позволяем выбирать неактивные фреймы, если проверка цен включена
            if (!this.disablePriceCheck && (!frame || !frame.isActive)) {
                return;
            }

            const normalizedSelected = this.selectedFrameGids.map(sgid => String(sgid));
            const isCurrentlySelected = normalizedSelected.includes(normalizedGid);

            if (!this.allowMultiSsp && this.isFrameSspBlocked(frame)) {
                return;
            }

            let next;

            if (isCurrentlySelected) {
                // Снимаем выбор с экрана
                next = normalizedSelected.filter((sgid) => sgid !== normalizedGid);
            } else {
                // Добавляем экран
                if (!this.allowMultiSsp && normalizedSelected.length > 0 && frame && frame.ssp) {
                    const selectedFrames = normalizedSelected.map(sgid => {
                        return this.frames.find(f => String(f.gid) === String(sgid));
                    }).filter(Boolean);

                    const selectedSSPs = new Set(selectedFrames.map(f => f.ssp).filter(Boolean));

                    if (selectedSSPs.size > 0 && !selectedSSPs.has(frame.ssp)) {
                        next = [normalizedGid];
                    } else {
                        next = [...normalizedSelected, normalizedGid];
                    }
                } else {
                    next = [...normalizedSelected, normalizedGid];
                }
            }

            this.$emit('update:selectedFrameGids', next);
        },
        // (removed hasExactSizeMatch — exact-size highlighting handled elsewhere if needed)
        isUnknownCity(frame) {
            const name = String(frame?.city ?? '').trim().toLowerCase();
            return name === 'неизвестно'
                || name === 'неизвестные'
                || name === 'unknown';
        },
        isFrameSspBlocked(frame) {
            if (this.allowMultiSsp || !frame?.ssp) {
                return false;
            }

            const selectedSsps = new Set(
                this.selectedFrameGids
                    .map(gid => this.frames.find(f => String(f.gid) === String(gid))?.ssp)
                    .filter(Boolean)
            );

            if (selectedSsps.size === 0) {
                return false;
            }

            return !selectedSsps.has(frame.ssp);
        },
        formatFrameDisplayLabel(frame) {
            if (!frame) return '';
            const street1 = String(frame.street_1_name || frame.street_1 || '').trim();
            const street2 = String(frame.street_2_name || frame.street_2 || '').trim();
            const streetPart = [street1, street2].filter(Boolean).join(' — ');
            const landmark = String(frame.landmark || '').trim();
            const type = String(frame.construction_type_label || frame.construction_type || '').trim();
            const extras = [];
            if (landmark) extras.push(landmark);
            if (type && type !== landmark) extras.push(type);
            const composed = [streetPart, extras.join(', ')].filter(Boolean).join(', ');
            if (composed) return composed;
            const fallback = String(frame.address || frame.city || '').trim();
            if (fallback) return fallback;
            return frame.gid != null && frame.gid !== '' ? `Экран #${frame.gid}` : '';
        },
        getFrameLabel(gid) {
            const frame = this.frames.find(f => String(f.gid) === String(gid));
            if (!frame) return `Экран #${gid}`;
            return this.formatFrameDisplayLabel(frame);
        },
        markerState(frame) {
            if (!this.disablePriceCheck && !frame?.isActive) {
                return 'unavailable';
            }

            if (this.isFrameSspBlocked(frame)) {
                return 'unavailable';
            }

            if (this.isSelected(frame?.gid)) {
                return 'selected';
            }

            return 'idle';
        },
        isMarkerClickable(frame) {
            return !this.readOnly
                && (this.disablePriceCheck || frame.isActive)
                && !this.isFrameSspBlocked(frame);
        },
        infoWindowOptions() {
            const options = { disableAutoPan: true };
            if (typeof google !== 'undefined' && google.maps?.Size) {
                options.pixelOffset = new google.maps.Size(0, -8);
            }

            return options;
        },
        focusFrame(frame) {
            const lat = Number(frame?.lat);
            const lng = Number(frame?.lon);
            const map = this.$refs.gmap?.$mapObject;
            if (!map || !Number.isFinite(lat) || !Number.isFinite(lng)) {
                return;
            }

            map.setZoom(16);
            map.panTo({ lat, lng });
        },
        clearFilters() {
            this.sizeFilter = [];
            this.organizationFilter = [];
            this.showOnlySelected = false;
            this.cityFilter = [];
            this.streetFilter = [];
            this.typeFilter = [];
            this.showTestFrames = false;
            this.showOnlyEnabled = true;
        },
        toggleOnlySelected() {
            if (this.selectedFrameGids.length === 0) {
                this.showOnlySelected = false;
                return;
            }

            this.showOnlySelected = !this.showOnlySelected;
        },
        selectAllVisible() {
            if (this.readOnly) {
                return;
            }

            const next = (this.sortedFrames || [])
                .filter((frame) => this.disablePriceCheck || frame.isActive)
                .filter((frame) => !this.isFrameSspBlocked(frame))
                .map((frame) => String(frame.gid));

            this.$emit('update:selectedFrameGids', next);
        },
        clearSelection() {
            if (this.readOnly) {
                return;
            }

            this.$emit('update:selectedFrameGids', []);
        },
        selectFilteredFrames() {
            if (this.readOnly || !this.canSelectFiltered) {
                return;
            }

            const next = new Set(this.selectedGidSet);
            this.selectableFilteredFrames.forEach((frame) => next.add(String(frame.gid)));
            this.$emit('update:selectedFrameGids', [...next]);
        },
        deselectFilteredFrames() {
            if (this.readOnly || !this.canDeselectFiltered) {
                return;
            }

            const visible = new Set(this.selectableFilteredFrames.map((frame) => String(frame.gid)));
            const next = [...this.selectedGidSet].filter((gid) => !visible.has(gid));
            this.$emit('update:selectedFrameGids', next);
        },
        openFullscreen(src) {
            this.fullscreenImageUrl = src;
        },
        closeFullscreen() {
            this.fullscreenImageUrl = null;
        },
        toggleMapFullscreen() {
            const root = this.$refs.mapRoot;
            if (!root) {
                return;
            }
            if (document.fullscreenElement === root) {
                document.exitFullscreen();
                return;
            }
            root.requestFullscreen().catch(() => {});
        },
        onFullscreenChange() {
            const active = document.fullscreenElement === this.$refs.mapRoot;
            if (!active) {
                this.sidebarResizeCleanup?.({ pointerId: null });
            }
            this.isMapFullscreen = active;
            this.$nextTick(() => {
                this.$refs.gmap?.resizePreserveCenter();
            });
        },
        startSidebarResize(event) {
            if (!this.isMapFullscreen || event.button !== 0) {
                return;
            }

            const handle = event.currentTarget;
            const root = this.$refs.mapRoot;
            const sidebar = this.$refs.sidebar;
            if (!root || !sidebar || !handle) {
                return;
            }

            event.preventDefault();
            handle.setPointerCapture(event.pointerId);

            const startX = event.clientX;
            const startWidth = this.sidebarWidthPx ?? sidebar.getBoundingClientRect().width;
            this.isResizingSidebar = true;

            const onMove = (moveEvent) => {
                const available = root.clientWidth;
                const min = Math.min(SIDEBAR_MIN_PX, available);
                const max = Math.max(min, available - MAP_MIN_PX);
                const next = startWidth + (moveEvent.clientX - startX);
                this.sidebarWidthPx = Math.min(Math.max(next, min), max);
                this.scheduleMapResize();
            };
            const stop = (endEvent) => {
                if (endEvent.pointerId != null && handle.hasPointerCapture?.(endEvent.pointerId)) {
                    handle.releasePointerCapture(endEvent.pointerId);
                }
                handle.removeEventListener('pointermove', onMove);
                handle.removeEventListener('pointerup', stop);
                handle.removeEventListener('pointercancel', stop);
                this.sidebarResizeCleanup = null;
                this.isResizingSidebar = false;
                this.scheduleMapResize();
            };

            this.sidebarResizeCleanup = stop;
            handle.addEventListener('pointermove', onMove);
            handle.addEventListener('pointerup', stop);
            handle.addEventListener('pointercancel', stop);
        },
        syncPoints() {
            this.pointLayer?.sync(this.points, this.pointsDraggable);
        },
        scheduleMapResize() {
            if (this.sidebarResizeRaf) {
                return;
            }

            this.sidebarResizeRaf = requestAnimationFrame(() => {
                this.sidebarResizeRaf = 0;
                this.$refs.gmap?.resizePreserveCenter();
            });
        },
    },
    watch: {
        weekGidKey: {
            immediate: true,
            handler() {
                this.refreshWeekActive();
            },
        },
        selectedFrameGids(gids) {
            if (this.showOnlySelected && (!Array.isArray(gids) || gids.length === 0)) {
                this.showOnlySelected = false;
            }
        },
        markerFrames(frames) {
            this.markerLayer?.setFrames(frames);
            if (this.hoveredId != null && !frames.some((frame) => String(frame.gid) === String(this.hoveredId))) {
                this.hoveredId = null;
            }
        },
        points: {
            deep: true,
            handler() {
                this.syncPoints();
            },
        },
        pointsDraggable() {
            this.syncPoints();
        },
    },
    mounted() {
        document.addEventListener('fullscreenchange', this.onFullscreenChange);
        this.$refs.gmap.$mapPromise.then((map) => {
            this.markerLayer = createFrameMarkerLayer(map, {
                onHover: (gid) => {
                    this.hoveredId = gid;
                },
                onClick: (gid) => {
                    this.toggleFrameSelection(gid);
                },
                onMapClick: (event) => {
                    const latLng = event?.latLng;
                    const lat = typeof latLng?.lat === 'function' ? latLng.lat() : latLng?.lat;
                    const lng = typeof latLng?.lng === 'function' ? latLng.lng() : latLng?.lng;
                    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
                    this.$emit('map-click', { lat, lng });
                },
            });
            this.markerLayer.setFrames(this.markerFrames);
            this.pointLayer = createPoiPointLayer(map, {
                onDragEnd: (index, position) => {
                    this.$emit('point-move', { index, ...position });
                },
            });
            this.syncPoints();
        }).catch(() => {});
    },
    beforeUnmount() {
        this.markerLayer?.destroy();
        this.pointLayer?.destroy();
        document.removeEventListener('fullscreenchange', this.onFullscreenChange);
        this.sidebarResizeCleanup?.({ pointerId: null });
        if (this.sidebarResizeRaf) {
            cancelAnimationFrame(this.sidebarResizeRaf);
            this.sidebarResizeRaf = 0;
        }
    },
};
</script>

<style scoped lang="scss">
.google-map {
    display: flex;
    justify-content: space-between;
    height: calc(100vh - 60px - 16px);

    &--fill {
        height: 100%;
    }

    &:fullscreen {
        width: 100%;
        height: 100%;
        background: #fff;
        box-sizing: border-box;

        .sidebar {
            flex: 0 0 auto;
        }

        .map-container {
            width: auto;
            flex: 1 1 auto;
            min-width: 0;
        }
    }

    &--resizing {
        user-select: none;
        cursor: col-resize;
    }

    .test-badge {
        background: #ede9fe;
        color: #5b21b6;
        font-size: 11px;
        padding: 2px 6px;
        border-radius: 4px;
        font-weight: 600;
        margin-left: 8px;
        white-space: nowrap;
    }

    .sidebar {
        width: 43%;
        padding: 18px;
        border: 1px solid #e5e7eb;
        background: #fff;
        display: flex;
        flex-direction: column;
        gap: 12px;
        border-radius: 8px;
        height: 100%;
        min-height: 0;

        .sidebar__head {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 8px;
            flex-shrink: 0;

            > div:first-child {
                font-weight: 800;
            }
        }

        .sidebar__head-actions {
            display: flex;
            align-items: center;
            gap: 8px;
            flex-shrink: 0;
        }

        .filters-toggle {
            position: relative;
            padding: 6px 12px;
            background: #f3f4f6;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            font-size: 12px;
            font-weight: 600;
            color: #374151;
            cursor: pointer;

            &:hover {
                background: #e5e7eb;
            }

            &.is-active {
                background: #dbeafe;
                border-color: #2563eb;
                color: #1d4ed8;
            }
        }

        .filters-toggle__dot {
            position: absolute;
            top: 4px;
            right: 4px;
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: #2563eb;
        }

        .filters-section {
            position: relative;
            z-index: 2;
            flex-shrink: 0;
            padding: 12px 0;
            border-bottom: 1px solid #e5e7eb;
            margin-bottom: 12px;
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 12px;
            align-items: end;
        }

        .packages-list {
            flex: 1;
            overflow-y: auto;
            min-height: 0;
        }

        .layer-row {
            align-items: flex-start;
        }

        .layer-toggle {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            margin-left: 8px;
            padding: 2px;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            background: #f3f4f6;
            color: #374151;
            cursor: pointer;

            img {
                width: 14px;
                height: 14px;
                transition: transform 0.2s ease;
            }

            &--open img {
                transform: rotate(180deg);
            }

            &:hover {
                background: #e5e7eb;
            }
        }

        .layer-expand {
            padding: 0 8px 8px 28px;
        }

        .layer-expand__title {
            margin: 8px 0 4px;
            font-weight: 600;
            color: #374151;
        }

        .layer-points {
            margin: 0;
            padding-left: 18px;
            color: #1e293b;
            font-weight: 500;
            line-height: 1.5;
        }

        .layer-note {
            margin: 4px 0;
            color: #6b7280;
        }

        .filter-group {
            display: flex;
            flex-direction: column;
            gap: 6px;
            min-width: 0;
        }

        .filter-group--wide {
            grid-column: 1 / -1;
        }

        .filter-group--checkbox {
            justify-content: flex-end;
        }

        .filter-group--toggles {
            flex-direction: row;
            align-items: stretch;
            gap: 12px;

            .only-selected-btn {
                flex: 1;
                width: auto;
            }
        }

        .only-selected-btn {
            width: 100%;
            padding: 6px 12px;
            background: #f3f4f6;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            font-size: 12px;
            color: #374151;
            cursor: pointer;
            transition: all 0.2s;

            &:hover:not(:disabled) {
                background: #e5e7eb;
            }

            &.is-active {
                background: #dbeafe;
                border-color: #2563eb;
                color: #1d4ed8;
            }

            &:disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }
        }

        .filter-label {
            font-size: 12px;
            font-weight: 500;
            color: #6b7280;
        }

        .filter-label--checkbox {
            display: flex;
            align-items: center;
            gap: 8px;
            cursor: pointer;
            user-select: none;
        }

        .filter-select {
            width: 100%;
            padding: 6px 10px;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            font-size: 14px;
            background: #fff;
            cursor: pointer;
            transition: border-color 0.2s;

            &:hover {
                border-color: #9ca3af;
            }

            &:focus {
                outline: none;
                border-color: #2563eb;
                box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
            }
        }

        .filter-select--multiple {
            min-height: 86px;
        }

        /* Custom city select styles moved to RTBCampaignForm (parent component) */

        .clear-filters-btn {
            grid-column: 1 / -1;
            padding: 6px 12px;
            background: #f3f4f6;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            font-size: 12px;
            color: #6b7280;
            cursor: pointer;
            transition: all 0.2s;

            &:hover:not(:disabled) {
                background: #e5e7eb;
                color: #374151;
            }

            &:disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }
        }

        .screens-list {
            flex: 1;
            overflow-x: hidden;
            overflow-y: auto;
            min-height: 0;
        }

        .screen-row {
            min-width: 0;
        }

        .screen-row__week {
            min-width: 0;
            padding: 0 8px 8px;
        }

        .checkbox-item {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 8px;
            border-radius: 6px;
            overflow: hidden;
            box-sizing: border-box;

            &:hover {
                background: #f8fafc;
            }

            &.inactive {
                opacity: 0.6;

                &:hover {
                    background: #f1f5f9;
                }
            }

            &.ssp-filtered {
                opacity: 0.5;

                .checkbox__name {
                    color: #9ca3af;
                }
            }

            &.in-package {
                background: linear-gradient(90deg, rgba(37, 99, 235, 0.03), transparent);
            }
        }

        .custom-checkbox {
            -webkit-appearance: none;
            appearance: none;
            display: inline-block;
            position: relative;
            width: 18px;
            height: 18px;
            border: 2px solid #cbd5e1;
            border-radius: 4px;
            background: #fff;
            cursor: pointer;
            flex-shrink: 0;
            transition: all 0.18s ease;
        }

        /* radio inputs should look circular */
        input[type="radio"].custom-checkbox {
            border-radius: 50%;
        }

        .custom-checkbox:checked {
            background: linear-gradient(180deg, #2563eb, #1d4ed8);
            border-color: transparent;
        }

        .custom-checkbox:disabled {
            background: #f1f5f9;
            border-color: #e2e8f0;
            cursor: not-allowed;
        }

        .checkbox {
            cursor: pointer;
            font-weight: 500;
            display: inline-flex;
            align-items: center;
            gap: 10px;
            flex: 1;
            min-width: 0;

            &.disabled {
                cursor: not-allowed;
                color: #9ca3af;
            }
        }

        .checkbox__label {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 8px;
            width: 100%;
            min-width: 0;
        }

        .checkbox__name {
            display: flex;
            align-items: center;
            gap: 6px;
            min-width: 0;
            overflow: hidden;
        }

        .checkbox__title {
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .checkbox__meta {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            flex-shrink: 0;
        }

        .frame-size {
            color: #6b7280;
            font-size: 12px;
            margin-left: 4px;
            flex-shrink: 0;

            &.frame-size--match {
                font-weight: 700;
                color: #2563eb;
            }
        }

        .inactive-badge {
            background: #fef3c7;
            color: #92400e;
            font-size: 11px;
            padding: 2px 6px;
            border-radius: 4px;
            font-weight: 500;
            margin-left: 8px;
        }

        .screen-warning {
            display: inline-flex;
            flex-shrink: 0;
            margin: 0;
            padding: 0;
            border: 0;
            background: transparent;
            cursor: help;
            line-height: 0;

            img {
                display: block;
                height: 12px;
                width: 12px;
            }
        }

        .screen-audience {
            display: block;
            flex-shrink: 0;
            width: 13px;
            height: 13px;
        }

        .package-badge {
            margin-left: 8px;
            font-size: 12px;
            color: #2563eb;
            background: #eff6ff;
            padding: 2px 6px;
            border-radius: 6px;
            font-weight: 600;
        }

        .screen-locate {
            display: inline-flex;
            flex-shrink: 0;
            margin: 0;
            padding: 0;
            border: 0;
            background: transparent;
            cursor: pointer;
            line-height: 0;

            img {
                width: 16px;
                height: 16px;
                display: block;
            }
        }

        .frame-id-badge {
            font-size: 11px;
            font-weight: 600;
            padding: 2px 6px;
            border-radius: 4px;
            background: #f3f4f6;
            color: #4b5563;
            white-space: nowrap;
            flex-shrink: 0;
        }

        .selected-info {
            margin-top: auto;
            flex-shrink: 0;
            color: #6b7280;

            p {
                margin: 0;
            }

            strong {
                color: #111827;
            }
        }

        .bulk-select {
            display: flex;
            gap: 8px;
            margin-top: 10px;
        }

        .bulk-select--above-list {
            flex-shrink: 0;
            gap: 6px;
            margin-top: 0;
            margin-bottom: 8px;

            .bulk-select__btn {
                padding: 2px 8px;
                font-size: 11px;
                line-height: 1.3;
            }
        }

        .bulk-select__btn {
            padding: 6px 10px;
            background: #f3f4f6;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            font-size: 12px;
            color: #374151;
            cursor: pointer;
            transition: background 0.2s, color 0.2s;

            &:hover:not(:disabled) {
                background: #e5e7eb;
            }

            &:disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }
        }
    }

    .sidebar-resizer {
        flex: 0 0 8px;
        align-self: stretch;
        cursor: col-resize;
        touch-action: none;
        position: relative;
        z-index: 3;

        &::before {
            content: '';
            position: absolute;
            top: 8px;
            bottom: 8px;
            left: 3px;
            width: 2px;
            border-radius: 1px;
            background: #d1d5db;
        }

        &:hover::before {
            background: #9ca3af;
        }
    }

    .map-container {
        position: relative;
        width: 55%;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        overflow: hidden;
        height: 100%;

        .map-fullscreen-btn {
            position: absolute;
            top: 10px;
            right: 10px;
            z-index: 2;
            padding: 6px 10px;
            background: #fff;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            font-size: 12px;
            line-height: 1.3;
            color: #374151;
            cursor: pointer;
            box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);

            &:hover {
                background: #f3f4f6;
            }
        }

        .vue-map-container {
            height: 100%;

            /* скрыть крестик у всех InfoWindow */
            ::v-deep(.gm-ui-hover-effect) {
                display: none !important;
            }

            /* при необходимости — более узко */
            ::v-deep(.gm-style .gm-style-iw-tc button[aria-label='Close']) {
                display: none !important;
            }

            /* InfoWindow content */
            .info-window-content {
                max-width: 260px;
                width: 100%;
            }

            .info-window-img {
                width: 100%;
                margin-bottom: 6px;
                display: block;
                object-fit: cover;
                flex-shrink: 0;
            }

            .info-window-heading {
                margin: 0;
                font-weight: 500;
                color: #111827;
            }

            .info-window-warning {
                margin: 0;
                font-size: 12px;
                color: #dc2626;
                font-weight: 500;
            }

            .info-window-unavailable {
                margin: 4px 0 0;
                font-size: 12px;
                color: #b45309;
                font-weight: 500;
            }

            .info-window-meta {
                margin: 0;
                font-size: 12px;
                color: #555;
            }

            /* Modal overlay styles (outside map container) */
            /* These are placed here due to scoped styles; selectors are global enough for modal */
        }
    }
}

.img-overlay {
    position: fixed;
    inset: 0;
    z-index: 10000;
    background: rgba(0, 0, 0, 0.65);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    animation: fadeIn 0.2s ease;

    .img-overlay-close {
        position: absolute;
        top: 12px;
        right: 12px;
        background: rgba(0, 0, 0, 0.6);
        color: #fff;
        border: none;
        width: 40px;
        height: 40px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        font-size: 20px;
        transition: background 0.2s;

        &:hover {
            background: rgba(0, 0, 0, 0.85);
        }
    }

    img {
        max-width: 90vw;
        max-height: 90vh;
        width: auto;
        height: auto;
        object-fit: contain;
        border-radius: 10px;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
        animation: zoomIn 0.2s ease;
    }
}
</style>